/** @format */

import { Actions, ofType } from '@ngrx/effects';
import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { DPLD_KEY, familyPageDropdownValues, plpDefaultSortMap } from '@plp/data-access/constants';
import {
  DestroyRef,
  Injectable,
  PLATFORM_ID,
  REQUEST_CONTEXT,
  TransferState,
  computed,
  inject,
} from '@angular/core';
import { DisplayableProductListData, ProductList } from '@plp/data-access/interfaces';
import { EMPTY, Observable, of, takeUntil } from 'rxjs';
import { HttpService, KillswitchService, Logger } from '@shared/data-access/services';
import {
  ProductListApiService,
  ProductListCompareService,
  ProductListMlService,
  ProductListUIService,
} from '@plp/data-access/services';
import { catchError, filter, mergeMap, tap } from 'rxjs/operators';
import {
  initialProductListState,
  plpResolverAPIDataInitialized,
  plpResolverOnLoadDataInitialized,
  plpResolverProductListFetchErrored,
  plpResolverSEOError,
  plpResolverSEONoCategories,
  productListLoadTriggered,
  secondPassProductListFetched,
  selectFeatureFlags,
} from '@plp/data-access/state';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';

import { PrunedProductListSEO } from '@shared/data-access/interfaces';
import { SeoService } from '@seo/data-access';
import { Store } from '@ngrx/store';
import { setCommonPageType } from '@state/data-access';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Injectable({
  providedIn: 'root',
})
export class ProductListPageResolveGuard {
  private readonly killSwitchService = inject(KillswitchService);
  private readonly log = inject(Logger);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly plpCompareService = inject(ProductListCompareService);
  private readonly plpMlService = inject(ProductListMlService);
  private readonly productListApiService = inject(ProductListApiService);
  private readonly productListUIService = inject(ProductListUIService);
  private readonly propagateToServer = inject<{
    PROPAGATE_ERROR_TO_SERVER: (propagatedError: { errorType: string; redirectUrl?: string }) => {
      errorType: string;
      redirectUrl?: string;
    };
  }>(REQUEST_CONTEXT, { optional: true })?.PROPAGATE_ERROR_TO_SERVER;

  private readonly seoService = inject(SeoService);
  private readonly state = inject(TransferState);
  private readonly store = inject(Store);
  private readonly destroyRef = inject(DestroyRef);
  private readonly actions$ = inject(Actions);
  private readonly httpService = inject(HttpService);

  private readonly featureFlags = this.store.selectSignal(selectFeatureFlags);

  private readonly compareCheckboxEnabled = computed(
    () => this.featureFlags().compareCheckboxEnabled,
  );

  fulfillmentFacetsEnabled = false;
  plpRedirectErrorMessage = 'PLP Redirect error';

  constructor() {
    this.store.dispatch(setCommonPageType({ pageType: 'plp' }));

    const featureFlags = this.killSwitchService.killSwitchesForCurrentChain();
    this.fulfillmentFacetsEnabled = featureFlags.fulfillmentFacetsEnabled;
  }

  resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    return this.seoService.getPrunedSEOCategory$(route.params['seoToken'], 1, 'f').pipe(
      mergeMap((seoCategory) => {
        if (seoCategory && Object.keys(seoCategory).length) {
          this.handlePageRedirect(seoCategory);

          const displayableProductListData = this.getDisplayableProductListDataFromState();

          // Only execute in the Server to avoid duplicated tags
          if (isPlatformServer(this.platformId)) {
            this.seoService.initializePageSEO(seoCategory, state.url.split('?')[0]);
          }

          if (isPlatformBrowser(this.platformId)) {
            this.seoService.handleCategoryListLoaded(seoCategory.breadCrumbDetails);
          }

          const pageNumber = this.productListUIService.getPageNumber(route.queryParams.pageNumber);

          const pageSize = this.productListUIService.getPageSize(route.queryParams.pageSize);

          const selectedFilters =
            this.productListApiService.formatFiltersFromURL(route.queryParams.filterFacets) ?? {};

          const selectedSort = this.getSelectedSort(seoCategory, route.queryParams.selectedSort);

          // Apply data from before product list call to state
          this.store.dispatch(
            plpResolverOnLoadDataInitialized({
              data: {
                displayableProductListData,
                pageNumber,
                pageSize,
                productListSEO: seoCategory,
                selectedFilters,
                selectedSort,
              },
            }),
          );

          // MAKE CALL FOR PRODUCT LIST DATA
          return this.makeCallForProductListData(
            seoCategory.catgroupId,
            pageNumber,
            pageSize,
            selectedFilters,
          );
        } else {
          this.store.dispatch(plpResolverSEONoCategories());
        }

        return of(null);
      }),
      catchError((error) => {
        if (
          isPlatformServer(this.platformId) &&
          error instanceof Error &&
          error?.message === this.plpRedirectErrorMessage
        ) {
          return EMPTY;
        }

        this.store.dispatch(plpResolverSEOError());

        this.log.error(
          `APPLICATION_ERROR:HTTP_ERROR: Error getting page by SEO TOKEN: ${route.params['seoToken']}. ${this.getErrorToPrint(error)}`,
        );

        return of(null);
      }),
    );
  }

  /**
   * Handles the redirection of the page based on the product list SEO information.
   * If the page type is 'c' (category) or 's' (static) or 'f' (family), it redirects to the corresponding SEO URL.
   * Otherwise, it dispatches an action to set the product list page as valid.
   *
   * @param productListSEO - The SEO information of the product list, which includes categories with page type and SEO URL.
   */
  private handlePageRedirect(productListSEO: PrunedProductListSEO): void {
    if (isPlatformBrowser(this.platformId) || !productListSEO.pageType || !productListSEO.seoUrl) {
      return;
    }

    const pageType = productListSEO.pageType;
    const seoUrl = productListSEO.seoUrl;
    const isRedirect = productListSEO.redirect;
    if (seoUrl && ((pageType === 'f' && isRedirect) || pageType === 'c' || pageType === 's')) {
      if (this.propagateToServer) {
        this.propagateToServer({
          errorType: 'redirect',
          redirectUrl: `/${pageType}/${seoUrl}`,
        });
      } else {
        this.httpService.redirect(`/${pageType}/${seoUrl}`);
      }

      throw new Error(this.plpRedirectErrorMessage, {
        cause: { errorType: 'redirect', redirectUrl: `/${pageType}/${seoUrl}` },
      });
    }
  }

  /**
   * Retrieves the displayable product list data from the state.
   *
   * @returns {void}
   */
  private getDisplayableProductListDataFromState(): DisplayableProductListData {
    return this.state.get(DPLD_KEY, initialProductListState.displayableProductListData);
  }

  /**
   * @description
   * Accepts an error and stringifies the important parts
   */
  private getErrorToPrint(error: unknown): string {
    try {
      return JSON.stringify(error, ['status', 'message', 'arguments', 'type', 'name']);
    } catch {
      return '';
    }
  }

  /**
   * @description
   * Make call for Product List for the PLP product Grid once the seo data is set
   */
  private makeCallForProductListData(
    catgroupId: number,
    pageNumber: number,
    pageSize: number,
    selectedFilters: Record<string, string[]>,
  ): Observable<ProductList | null> {
    const queryParams = this.productListApiService.setProductListParamDataForApi({
      selectedCategoryId: catgroupId,
      searchTerm: null,
      pageNumber,
      selectedFilters,
      pageSize,
      includeFulfillmentFacets: this.fulfillmentFacetsEnabled,
    });

    return this.productListApiService.getInitialProductListData$(queryParams, 1).pipe(
      filter((productList: ProductList | null) => !!productList),
      tap((productList: ProductList | null) => {
        const landingPageURL = productList?.searchVO?.landingPageURL;
        if (landingPageURL) {
          if (isPlatformBrowser(this.platformId)) {
            this.productListUIService.redirectToPLPOrPDP(landingPageURL);
            return;
          }

          // If the landingPageURL is present, redirect to the PLP, /f/ -> /p/ (PDP) server side
          if (landingPageURL.startsWith('/p/')) {
            if (this.propagateToServer) {
              this.propagateToServer({
                errorType: 'redirect',
                redirectUrl: `${landingPageURL}`,
              });
            } else {
              this.httpService.redirect(`${landingPageURL}`);
            }

            throw new Error(this.plpRedirectErrorMessage, {
              cause: {
                errorType: 'redirect',
                redirectUrl: `${landingPageURL}`,
              },
            });
          }
        }
        this.plpMlService.generateNewMlSearchEventParentUUID();

        const compareBarProducts = this.compareCheckboxEnabled()
          ? this.plpCompareService.getCompareListFromSessionStorage()
          : [];

        this.store.dispatch(
          plpResolverAPIDataInitialized({
            data: {
              productList,
              compareBarProducts,
            },
          }),
        );

        // Trigger second pass
        this.productListApiService
          .getSecondPassProductListData$(queryParams, 1)
          .pipe(
            takeUntil(this.actions$.pipe(ofType(productListLoadTriggered))),
            takeUntilDestroyed(this.destroyRef),
          )
          .subscribe({
            next: (secondPassProductList) => {
              if (secondPassProductList) {
                this.store.dispatch(
                  secondPassProductListFetched({ productList: secondPassProductList }),
                );
              }
            },
            error: (err) => {
              this.handleProductListError(err, catgroupId, true);
            },
          });
      }),
      catchError((error) => {
        if (
          isPlatformServer(this.platformId) &&
          error instanceof Error &&
          error?.message === this.plpRedirectErrorMessage
        ) {
          return EMPTY;
        }

        this.handleProductListError(error, catgroupId);

        return of(null);
      }),
    );
  }

  private handleProductListError(error: unknown, catgroupId: number, isSecondPass = false) {
    this.productListApiService.logProductListApiError(
      error,
      isSecondPass ? 'plp-guard:second-pass' : 'plp-guard',
      { catgroupId },
    );

    const pl = this.productListApiService.getProductListFromTransferState();
    if (pl) {
      this.store.dispatch(plpResolverProductListFetchErrored({ fallbackProductList: pl }));
    }
  }

  private getSelectedSort(
    productListSEO: PrunedProductListSEO,
    qpSelectedSort: string | undefined,
  ) {
    const defaultSortMap = plpDefaultSortMap;

    const sortDropdownValues = familyPageDropdownValues;

    const queryParamSortToNumber: number = qpSelectedSort ? parseInt(qpSelectedSort, 10) : 0;
    const sortBasedOnQueryParam = {
      label: sortDropdownValues[queryParamSortToNumber]?.label,
      value: queryParamSortToNumber,
    };

    const defaultSortFromSEO: number = defaultSortMap[productListSEO.longDescription];
    const sortBasedOnSEOData = {
      label: sortDropdownValues[defaultSortFromSEO]?.label,
      value: defaultSortFromSEO,
    };

    return qpSelectedSort ? sortBasedOnQueryParam : sortBasedOnSEOData;
  }
}
